import {RequestQueryBuilder, SCondition} from '@nestjsx/crud-request';
import {TorchUserService} from '../../@users';
import {Injectable} from '@angular/core';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import {HttpClient} from '@angular/common/http';
import {Burly} from 'kb-burly';
import {Observable, of} from 'rxjs';
import {PaginationResponse} from '../../../responses';
import {map, switchMap, tap} from 'rxjs/operators';
import {TrackedUnitResponse} from '../responses';
import {RogueCase, TrackedUnit} from '../models';
import {Angulartics2} from 'angulartics2';
import {CachedReplay} from '../../../../app/utils/cached-replay.utils';

@Injectable({
  providedIn: 'root',
})
export class TorchUnitTrackingService {
  private listeners: TrackedUnitsChangedListener[] = [];
  private countForUserCache = new CachedReplay<number>();
  private countForUserSource: Observable<number>;

  constructor(
    protected config: TorchAPIConfiguration,
    private httpClient: HttpClient,
    private angulartics2: Angulartics2,
    private userService: TorchUserService
  ) {
    this.countForUserSource = this.getCountForUser();
  }

  public getTrackedUnits(
    pageSize: number = 50,
    currentPage: number = 0,
    sortDirection: 'ASC' | 'DESC' | '' = 'DESC',
    sortBy: string = 'id',
    filters: SCondition | null
  ): Observable<TrackedUnitResponse> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }

    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/unit-tracking')
      .appendRawQueryString(qb.query(false))
      .get;


    return this.httpClient
      .get<PaginationResponse>(endpoint)
      .pipe(
        map((response) =>
          response.data
            ? new TrackedUnitResponse(response.data, response.total)
            : null
        )
      );
  }

  getTrackedUnitsByUserAndCPN(filter: string[] | string) {
    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/unit-tracking')
      .addSegment('/user-cpn-units');

    finalURL = endpoint.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<TrackedUnit[]>(finalURL);
  }

  /**
   * Get total tracked units for the current user
   */
  public getTotalTrackedUnits(): Observable<number> {
    return this.countForUserCache.sharedReplayTimerRefresh(this.countForUserSource, 1, 5000);
  }

  /**
   * Delete a TrackedUnit
   *
   * @param trackedUnit - TrackedUnit object to be deleted
   */
  public deleteTrackedUnit(trackedUnit: TrackedUnit): Observable<any> {
    if (!trackedUnit.id) {
      return of(false);
    } else {
      this.angulartics2.eventTrack.next({
        action: 'Delete Tracked Unit',
        properties: {
          companyPartNumber: trackedUnit.companyPartNumber,
        },
      });

      const endpoint = Burly(this.config.rootUrl).addSegment(
        '/unit-tracking/' + trackedUnit.id
      );

      return this.httpClient
        .delete(endpoint.get)
        .pipe(tap(() => {
          this.notifyListeners();
        }));
    }
  }

  /**
   * Create a single TrackedUnit
   *
   * @param trackedUnit - New TrackedUnit object
   */
  public createTrackedUnit(trackedUnit: TrackedUnit): Observable<TrackedUnit> {
    this.angulartics2.eventTrack.next({
      action: 'Create Tracked Unit',
      properties: {
        companyPartNumber: trackedUnit.companyPartNumber,
      },
    });

    const endpoint = Burly(this.config.rootUrl).addSegment('/unit-tracking');
    return this.httpClient.post<TrackedUnit>(endpoint.get, trackedUnit).pipe(
      tap(() => {
        this.countForUserCache.invalidate();
        this.notifyListeners();
      })
    );
  }

  public updateTrackedUnit(trackedUnit: TrackedUnit) {
    this.angulartics2.eventTrack.next({
      action: 'Update Tracked Unit',
      properties: {
        companyPartNumber: trackedUnit.companyPartNumber,
      },
    });

    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/unit-tracking')
      .addSegment('/' + trackedUnit.id)
      .get;


    return this.httpClient.patch<TrackedUnit>(endpointURL, trackedUnit);
  }

  public addListener(listener: TrackedUnitsChangedListener) {
    if (!this.listeners.includes(listener)) {
      this.listeners.push(listener);
    }
  }

  public getTrackedUnitsCount(filter?: string | string[]): Observable<number> {
    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/unit-tracking')
      .addSegment('/count');

    finalURL = endpoint.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<number>(finalURL);
  }

  /**
   * Send an email to whomever entered the tracked unit
   *
   * @param trackedUnit
   */
  public sendTrackedUnitEmail(trackedUnit: TrackedUnit) {
    return this.userService.getUser(trackedUnit.enteredBy).pipe(
      switchMap(user => {
        const endpointURL = Burly(this.config.rootUrl)
          .addSegment('/email/newTrackedUnitMessage')
          .get;

        return this.httpClient.post(endpointURL, {
          trackedUnit,
          recipientName: user.displayName,
          recipientEmailAddress: trackedUnit.enteredBy,
        });
      })
    );
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => listener.unitsChanged());
  }

  private getCountForUser() {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/unit-tracking')
      .addSegment('/count-for-user')
      .get;

    return this.httpClient.get<number>(endpoint).pipe(
      map((response) => (!!response ? response : 0)),
    );
  }
}

export interface TrackedUnitsChangedListener {
  unitsChanged();
}
